import {Injectable} from '@angular/core';
import {ObjectUtils} from "@shared/util/general/object-utils";
import {AceLoggerService} from "@logging/ace-logger.service";
import {AceLogger} from "@logging/ace-logger";

interface Cache {
  [key: string]: any
}

@Injectable({
  providedIn: 'root'
})
export class WebBrowserCommunicationService {

  public static readonly KEYS = {
    USER_DATA: 'user_data',
    SITES: 'sites',
    CLIENTS: 'clients'
  }

  private readonly logger: AceLogger;
  private readonly KEY = 'shareData';
  private readonly PREVIOUSLY_ACCESSED_CHILD_TAB_IDS_KEY = 'peak_cache_accessed_browser_tab_ids';
  private readonly browserTabIdManager: BrowserTabIdManager;

  private parentCache: Cache = null;

  constructor(logService: AceLoggerService) {
    this.logger = logService.create(WebBrowserCommunicationService.name);
    this.browserTabIdManager = new BrowserTabIdManager();
    this.initStore();
  }

  public update(key: string, value: any): void {
    this.getStore()[key] = value;
  }

  public delete(key: string): void {
    delete this.getStore()[key];
  }

  public getFromParent(key: string): any {
    const parentStore = this.getParentStore();
    const value = parentStore[key];
    delete parentStore[key];
    return value;
  }

  private getStore(): Cache {
    return window[this.KEY];
  }

  private initStore(): void {
    window[this.KEY] = {};
  }

  private getParentStore(): Cache {
    if (this.parentCache == null) {
      const parentCache: Cache = {};
      if (window?.opener != null) {
        try {
          const openerCache = window.opener[this.KEY];
          if (openerCache != null) {
            const browserTabId = this.browserTabIdManager.getBrowserTabId();
            // Access the cache on the parent window only once and record that it has been accessed.
            // This is so that if a user refreshes, the cached information is not used
            if (!window.opener[this.PREVIOUSLY_ACCESSED_CHILD_TAB_IDS_KEY]?.[browserTabId]) {
              if (window.opener[this.PREVIOUSLY_ACCESSED_CHILD_TAB_IDS_KEY] == null) {
                window.opener[this.PREVIOUSLY_ACCESSED_CHILD_TAB_IDS_KEY] = {};
              }
              window.opener[this.PREVIOUSLY_ACCESSED_CHILD_TAB_IDS_KEY][browserTabId] = true;

              Object.values(WebBrowserCommunicationService.KEYS).forEach(key => {
                parentCache[key] = ObjectUtils.cloneDeep(openerCache[key]);
                this.update(key, ObjectUtils.cloneDeep(openerCache[key]));
              });

              this.logger.info('Initialising cache from parent window');
            } else {
              this.logger.info('Not initialising cache from parent window as have already accessed it before with browser tab id', browserTabId);
            }
          } else {
            this.logger.info('Not initialising cache from parent window as window.opener\'s cache is not set');
          }
        } catch (e) {
          this.logger.info('Not initialising cache from parent window as there was an error accessing it');
          this.logger.trace('The error was', e);
        }
      } else {
        this.logger.info('Not initialising cache from parent window as window.opener is not set');
      }
      this.parentCache = parentCache;
    }

    return this.parentCache;
  }
}

class BrowserTabIdManager {

  private readonly WINDOW_ID_PREFIX = 'peak';
  private readonly WINDOW_ID_SEPARATOR = ':';

  constructor() {
    if (!window.name?.trim()) {
      window.name = this.WINDOW_ID_PREFIX + this.WINDOW_ID_SEPARATOR + (Math.random() * 0xffffffff >>> 0);
    }
  }

  getBrowserTabId(): string {
    return window.name;
  }
}
